import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

function Card({ children, style, ...props }) {
  return (
    <div style={{
      background: 'white', border: '1px solid var(--border)',
      borderRadius: 12, ...style,
    }} {...props}>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const configs = {
    not_set:          { label: 'Not set up',           bg: '#F3F4F6', color: '#6B7280' },
    pending_approval: { label: 'Under review',          bg: '#FEF3C7', color: '#92400E' },
    pending_stripe:   { label: 'Stripe — in progress',  bg: '#DBEAFE', color: '#1E40AF' },
    active:           { label: 'Active',                bg: '#D1FAE5', color: '#065F46' },
  };
  const cfg = configs[status] || configs.not_set;
  return (
    <span style={{
      fontSize: '0.78rem', fontWeight: 700,
      padding: '0.3rem 0.8rem', borderRadius: 20,
      background: cfg.bg, color: cfg.color,
    }}>{cfg.label}</span>
  );
}

export default function Payouts() {
  const { restaurant } = useAuth();
  const [payout, setPayout]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);

  const load = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    const data = await portalFetch('/payout-setup');
    if (data) setPayout(data);
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_complete') === '1') {
      setSuccess('Stripe account connected successfully. Your payout setup is pending final activation.');
      load();
      window.history.replaceState({}, '', '/payouts');
    }
    if (params.get('refresh') === '1') {
      setError('Stripe onboarding was interrupted. Please try again.');
      window.history.replaceState({}, '', '/payouts');
    }
  }, []); // eslint-disable-line

  // ── Stripe Connect ────────────────────────────────────────────────────────
  const handleStripeConnect = async (payoutMethod = 'stripe_connect') => {
    setSubmitting(true);
    setError(null);
    try {
      const { getValidToken } = await import('../lib/supabase');
      const token = await getValidToken();
      const res = await fetch('https://tablechat-mvp.vercel.app/api/portal/payout-setup', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: payoutMethod }),
      });
      const result = await res.json();
      if (result?.onboarding_url) {
        window.location.href = result.onboarding_url;
      } else {
        setError(`Stripe error: ${result?.details || result?.error || 'Unknown error'}`);
        setSubmitting(false);
      }
    } catch (err) {
      setError(`Request failed: ${err.message}`);
      setSubmitting(false);
    }
  };

const inputStyle = (hasError) => ({
    width: '100%', padding: '0.65rem 0.8rem',
    border: `1.5px solid ${hasError ? '#FCA5A5' : 'var(--border)'}`,
    borderRadius: 8, fontSize: '0.9rem',
    fontFamily: 'inherit', background: 'white',
    outline: 'none', boxSizing: 'border-box',
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 32, height: 32, border: '2px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 0.8rem' }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading payout settings...</p>
        </div>
      </div>
    );
  }

  const isActive  = payout?.payout_status === 'active';
  const isPending = ['pending_approval', 'pending_stripe'].includes(payout?.payout_status);
  const isNotSet  = !payout?.payout_method || payout?.payout_status === 'not_set';

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, margin: 0 }}>Payouts</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 4 }}>{restaurant?.name}</p>
      </div>

      {success && (
        <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '0.9rem 1.2rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.9rem', color: '#065F46', fontWeight: 600 }}>✓ {success}</span>
          <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontSize: '1.1rem' }}>×</button>
        </div>
      )}
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '0.9rem 1.2rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.9rem', color: '#DC2626', fontWeight: 600 }}>⚠️ {error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '1.1rem' }}>×</button>
        </div>
      )}

      {/* Current Status */}
      <Card style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Payout Status</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <StatusBadge status={payout?.payout_status || 'not_set'} />
              {payout?.payout_method && (
                <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                  {payout.payout_method === 'stripe_connect' ? '💳 Stripe Connect' : '🏦 Bank Transfer'}
                </span>
              )}
            </div>
          </div>
          {isActive && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>Payout Schedule</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)' }}>
                {payout.payout_method === 'stripe_connect' ? 'Weekly — every Monday' : 'Weekly — every Monday via ACH'}
              </div>
            </div>
          )}
        </div>

        {isActive && payout.payout_method === 'bank_transfer' && payout.bank_account_last4 && (
          <div style={{ marginTop: '1rem', padding: '0.8rem 1rem', background: 'var(--warm)', borderRadius: 8 }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--ink)' }}>
              <strong>{payout.bank_account_name}</strong> · Account ending ····{payout.bank_account_last4}
            </span>
          </div>
        )}
        {isActive && payout.payout_method === 'stripe_connect' && (
          <div style={{ marginTop: '1rem', padding: '0.8rem 1rem', background: 'var(--warm)', borderRadius: 8 }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--ink)' }}>
              Stripe account <strong>{payout.stripe_connect_account_id}</strong> · Direct payouts enabled
            </span>
          </div>
        )}
        {isPending && (
          <div style={{ marginTop: '1rem', padding: '0.8rem 1rem', background: '#FEF3C7', borderRadius: 8 }}>
            <span style={{ fontSize: '0.85rem', color: '#92400E' }}>
              {payout.payout_status === 'pending_approval'
                ? `⏳ Bank account (····${payout.bank_account_last4}) is under review. You'll be notified once payouts are activated (usually within 1 business day).`
                : '⏳ Complete your Stripe onboarding to activate payouts. If you were interrupted, click "Resume Stripe setup" below.'}
            </span>
          </div>
        )}
      </Card>

      {payout?.payout_status === 'pending_stripe' && !isActive && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button onClick={() => handleStripeConnect('stripe_connect')} disabled={submitting}
            style={{ padding: '0.75rem 1.5rem', background: '#635BFF', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Redirecting...' : 'Resume Stripe setup →'}
          </button>
        </div>
      )}

      {(isNotSet || selectedMethod) && !isPending && (
        <>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>
            {isNotSet ? 'Set up payouts' : 'Change payout method'}
          </h2>

          {!selectedMethod && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <Card style={{ padding: '1.5rem', cursor: 'pointer', border: '2px solid var(--border)', transition: 'border-color 0.15s' }}
                onClick={() => setSelectedMethod('stripe_connect')}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--orange)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>💳</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem', color: 'var(--ink)' }}>Connect Stripe Account</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5 }}>Already have a Stripe account? Connect it directly for automated weekly payouts with full visibility in your Stripe dashboard.</div>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {['Automated', 'Instant visibility', 'Stripe dashboard'].map(tag => (
                    <span key={tag} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: 20, background: '#EEF2FF', color: '#4F46E5' }}>{tag}</span>
                  ))}
                </div>
              </Card>

              <Card style={{ padding: '1.5rem', cursor: 'pointer', border: '2px solid var(--border)', transition: 'border-color 0.15s' }}
                onClick={() => setSelectedMethod('bank_transfer')}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--orange)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🏦</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem', color: 'var(--ink)' }}>Bank Transfer (ACH)</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5 }}>Connect your bank account through Stripe's secure onboarding. Takes 2–5 minutes. Money arrives in your bank every Monday.</div>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {['Secure onboarding', 'Every Monday', 'Direct deposit'].map(tag => (
                    <span key={tag} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: 20, background: '#F0FDF4', color: '#166534' }}>{tag}</span>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {selectedMethod === 'stripe_connect' && (
            <Card style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.5rem' }}>💳</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Connect with Stripe</h3>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                You'll be redirected to Stripe to connect your existing account or create a new one. This takes 2–5 minutes. Your payouts will go directly to your Stripe account every Monday.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => handleStripeConnect('stripe_connect')} disabled={submitting}
                  style={{ padding: '0.75rem 1.5rem', background: '#635BFF', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Redirecting to Stripe...' : 'Connect Stripe Account →'}
                </button>
                <button onClick={() => setSelectedMethod(null)}
                  style={{ padding: '0.75rem 1.2rem', background: 'white', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                  Back
                </button>
              </div>
            </Card>
          )}

          {selectedMethod === 'bank_transfer' && (
            <Card style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🏦</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Connect Your Bank Account</h3>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                You'll be redirected to Stripe's secure onboarding to verify your identity and add your bank account details. This takes 2–5 minutes. Payouts will be sent to your bank account every Monday.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => handleStripeConnect('bank_transfer')} disabled={submitting}
                  style={{ padding: '0.75rem 1.5rem', background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Redirecting to Stripe...' : 'Connect Bank Account →'}
                </button>
                <button onClick={() => setSelectedMethod(null)}
                  style={{ padding: '0.75rem 1.2rem', background: 'white', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                  Back
                </button>
              </div>
            </Card>
          )}
        </>
      )}

      {(isActive || isPending) && !selectedMethod && (
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={() => { setSelectedMethod(null); setPayout(prev => ({ ...prev, payout_status: 'not_set', payout_method: null })); }}
            style={{ fontSize: '0.82rem', color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
            Change payout method →
          </button>
        </div>
      )}
    </div>
  );
}


